import {
  AfterViewInit,
  ApplicationRef,
  Component,
  ElementRef,
  Injector,
  Input,
  NgZone,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation,
} from "@angular/core";
import {
  ProfilePromiseActionService,
  SystemConfigPromiseService,
  TraderGroupLookupService,
} from "shared/cubiq-services";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { TranslateService } from "@ngx-translate/core";
import { CellContextMenuEvent, GridOptions, Optional, RowNode } from "ag-grid-community";
import { BmarkBaseRiskBlotterComponent } from "app/bfx-bmark-summary/blotters/spot-risk/bmark-base-risk-blotter.component";
import { BmarkFwdRiskInstrumentRendererComponent } from "app/bfx-bmark-summary/blotters/spot-risk/renderers/bmark-fwd-risk-instrument-renderer.component";
import { BmarkFwdRiskTotalsRendererComponent } from "app/bfx-bmark-summary/blotters/spot-risk/renderers/bmark-fwd-risk-totals-renderer.component";
import { BmarkFwdRiskTraderGroupRendererComponent } from "app/bfx-bmark-summary/blotters/spot-risk/renderers/bmark-fwd-risk-trader-group-renderer.component";
import { BmarkFwdRiskContextMenuService } from "app/bfx-bmark-summary/blotters/spot-risk/services/bmark-fwd-risk-context-menu.service";
import { BmarkFwdRiskDataService } from "app/bfx-bmark-summary/blotters/spot-risk/services/bmark-fwd-risk-data.service";
import { BmarkSummaryRiskFilterType } from "app/bfx-bmark-summary/models/bmark-summary-risk-filter-type.enum";
import { BlotterType } from "shared/cubiq-model-multi-asset";
import { COLUMN_BMARK_INSTRUMENT } from "shared/cubiq-services/model/BaseBmarkRiskTableDefinitionFactory";
import { BmarkFwdRiskDisplayModel } from "shared/cubiq-services/model/benchmarks/BmarkSpotRiskDisplay.model";
import {
  BmarkFwdRiskTableDefinitionFactory,
  FWD_RISK_BLOTTER_TYPE,
} from "shared/cubiq-services/model/BmarkFwdRiskTableDefinitionFactory";
import { BfxColDef } from "shared/cubiq-services/model/TableDefinition";
import { provideBlotter } from "../../../bfx-base-blotter/bfx-base-blotter";
import { BfxBaseBlotterColumnSelectorService } from "../../../bfx-base-blotter/bfx-base-blotter-columnselector.service";
import { BfxBlotterFilterService } from "../../../bfx-blotter-filter/bfx-blotter-filter.service";
import { BfxMouseModifierKeyService } from "../../../bfx-common/services/bfx-mouse-modifier-key.service";
import { BfxSaveProfileNotificationService } from "../../../bfx-common/services/bfx-save-profile-notification.service";
import { BfxUserProfileService } from "../../../bfx-common/services/bfx-user-profile.service";
import { GoldenLayoutService } from "../../../golden-layout/golden-layout-service";

/**
 * Forward risk blotter (normally wrapped in a bmark-summary-spot-risk).
 * Uses generic risk view-based functionality but has
 * specific fwd-based services for data, table definition and context menu
 */

@Component({
  selector: "bmark-fwd-risk-blotter",
  templateUrl: "./html/bmark-fwd-risk-blotter.html",
  encapsulation: ViewEncapsulation.None,
  providers: [provideBlotter(BmarkFwdRiskBlotterComponent)],
  viewProviders: [BfxBaseBlotterColumnSelectorService],
  standalone: false,
})
export class BmarkFwdRiskBlotterComponent
  extends BmarkBaseRiskBlotterComponent
  implements AfterViewInit, OnChanges
{
  /** Risk filter is defined at summary level and passed down */
  @Input() riskFilter: BmarkSummaryRiskFilterType;

  constructor(
    @Optional() _traderGroupLookup: TraderGroupLookupService,
    bfxBlotterElement: ElementRef,
    applicationRef: ApplicationRef,
    injector: Injector,
    public fwdService: BmarkFwdRiskDataService,
    zone: NgZone,
    _tableDefinitionFactory: BmarkFwdRiskTableDefinitionFactory,
    layoutService: GoldenLayoutService,
    translationService: TranslateService,
    blotterFilterService: BfxBlotterFilterService,
    columnSelectorService: BfxBaseBlotterColumnSelectorService,
    profileActionService: ProfilePromiseActionService,
    sysConfigService: SystemConfigPromiseService,
    mouseModifierKeyService: BfxMouseModifierKeyService,
    saveProfileNotificationService: BfxSaveProfileNotificationService,
    userProfileService: BfxUserProfileService,
    _contextMenuServices: BmarkFwdRiskContextMenuService,
  ) {
    super(
      _traderGroupLookup,
      bfxBlotterElement,
      applicationRef,
      injector,
      fwdService,
      zone,
      _tableDefinitionFactory,
      layoutService,
      translationService,
      blotterFilterService,
      columnSelectorService,
      profileActionService,
      sysConfigService,
      mouseModifierKeyService,
      saveProfileNotificationService,
      userProfileService,
      _contextMenuServices,
    );
    // Change Instrument renderer for Fwd intruments and all total renderers (so we can invert intent of spot)
    const fc = this.frameworkComponents;
    fc.bmarkRiskInstrumentRenderer = BmarkFwdRiskInstrumentRendererComponent;
    fc.bmarkSpotRiskTotalsRenderer = BmarkFwdRiskTotalsRendererComponent;
    fc.bmarkSpotRiskTraderGroupRenderer = BmarkFwdRiskTraderGroupRendererComponent;
  }

  protected override isExternalColumn(colDef: BfxColDef): boolean {
    return false;
  }

  protected createsGridOptions(): GridOptions {
    let gridOptions = super.createsGridOptions();

    gridOptions.rowSelection = "none";
    return gridOptions;
  }

  public sortChanged(event) {
    logger.out.info("fwdrk-c - Sort changed - refreshing instrument column");
    this.refreshInstrumentRenderers();
  }

  protected override gridDataUpdated(): void {
    logger.out.info("fwdrk-c - gridDataUpdated  - refreshing instrument column");
    super.gridDataUpdated();
    this.refreshInstrumentRenderers();
  }

  /**
   * Override so that rows are not selected on context menu
   * @param event
   */
  public override onContextMenuSelectRow(event: CellContextMenuEvent) {
    this.gridOptions.api.deselectAll();
  }

  protected refreshInstrumentRenderers(): void {
    this.gridOptions.api.refreshCells({ columns: [COLUMN_BMARK_INSTRUMENT], force: true });
  }

  /**
   * Get blotter type - used when saving configuration
   * @returns
   */
  public override getBlotterType(): BlotterType {
    return BlotterType.FWD_RISK;
  }

  /**
   * Get blotter type - used when saving configuration
   * @returns
   */
  public override getBlotterTypeStr(): string {
    return FWD_RISK_BLOTTER_TYPE;
  }

  /**
   * Pick up changes to risk filter
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes["riskFilter"] != null) {
      this.updateRiskFilter();
    }
  }

  /**
   * Ensure grid is refiltered after filter type change
   */
  private updateRiskFilter() {
    if (this.gridOptions.api) {
      this.gridOptions.api.onFilterChanged();
      this.afterFilteringCompleted();
    }
  }

  /**
   * For Forward risk we want to do external filtering when riskType != ALL
   * @returns
   */
  public override isExternalFilterPresent(): boolean {
    return this.riskFilter != BmarkSummaryRiskFilterType.ALL;
  }

  public override doesExternalFilterPass(node: RowNode): boolean {
    const data: BmarkFwdRiskDisplayModel = node.data;
    if (data) {
      switch (this.riskFilter) {
        case BmarkSummaryRiskFilterType.WARNING:
          return data.isPositionAlert || data.isPositionWarning;
        case BmarkSummaryRiskFilterType.ALERT:
          return data.isPositionAlert;
        case BmarkSummaryRiskFilterType.ALL:
        default:
          return true;
      }
    }
  }
}
